import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Notation, NucleotideType} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import {getSanitizedBasesAndSanitizationErrors} from 'biojs/Util';
import {OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE} from 'constants/ModelTypeConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {isFullUTFlexibilityEnabled} from 'modbio/util';
import ColorPicker from 'molbio/components/ColorPicker';
import {getBaseCoercionWarningText} from 'molbio/MolBioUtil';
import {NucleotideTypeToggle} from 'molbio/NucleotideTypeToggle';
import ResidueNotationDropdown from 'molbio/ResidueNotationDropdown';
import {
  trackEnterValueInNucleotideCreateModal,
  trackNucleotideCreateModalSubmit,
} from 'molbio/SequenceAnalyticsHelper';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle, LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import * as net from 'util/Net';

interface Props {
  initialFolderId?: string;
  onRequestClose: () => void;
}

function CreateOligoModal({initialFolderId, onRequestClose}: Props): JSX.Element {
  const [bases, setBases] = useState('');
  // track prevBases to only log analytic event if value changes after hover
  const [prevBases, setPrevBases] = useState(bases);
  const [color, setColor] = useState('');
  const [folderId, setFolderId] = useState(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [name, setName] = useState('');
  // track prevName to only log analytic event if value changes after hover
  const [prevName, setPrevName] = useState(name);
  const [nucleotideType, setNucleotideType] = useState(NucleotideType.DNA);
  const [notation, setNotation] = useState(Notation.NATURAL_BASES);
  const [customSyntaxId, setCustomSyntaxId] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [basesCoerced, setBasesCoerced] = useState(false);
  const [tagSchemaId, setTagSchemaId] = useState<string>();
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);

  const disabled = name.trim().length === 0 || folderId == null || posting || bases.trim().length === 0;
  function logIfFieldValueChange(
    newVal: string,
    curVal: string,
    curValSetter: React.Dispatch<React.SetStateAction<string>>,
    fieldName: string
  ): void {
    if (curVal !== newVal) {
      trackEnterValueInNucleotideCreateModal(`${nucleotideType} oligo`, fieldName);
      curValSetter(newVal);
    }
  }
  async function createOligo(): Promise<void> {
    trackNucleotideCreateModalSubmit(`${nucleotideType} oligo`);
    setPosting(true);
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId!);
    let studyIds: Array<string> = [];
    if (studySearchItems.length) {
      studyIds = studySearchItems.map((studyItem) => studyItem.id);
    }
    try {
      const {id} = await net.postJSONPromise({
        url: '/1/api/oligos/',
        data: {
          bases,
          color,
          folderId,
          name,
          nucleotideType,
          notation,
          customSyntaxId,
          tagSchemaId,
          studyIds,
        },
      });
      FolderDAOCache.invalidateFolderContents(folderId);
      onRequestClose();
      FileStates.get().openFolderItem(id);
    } catch (err) {
      setPosting(false);
      throw err;
    }
  }

  return (
    <Modal
      title="Create oligo"
      onRequestClose={onRequestClose}
      onRequestSubmit={createOligo}
      buttons={[{text: 'Create', submit: true, disabled}]}
    >
      <FormGroup>
        <TextField
          label="Entity name"
          labelStyle={LabelStyle.LABEL}
          autoFocus={true}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          onBlur={() => {
            logIfFieldValueChange(name, prevName, setPrevName, 'name');
          }}
          dataTestAttrs={{component: 'CreateOligoModal', element: 'nameInput'}}
        />
      </FormGroup>

      <Row>
        <Col sm={BenchlingConfig.STUDIES_ENABLE ? 6 : 12}>
          <FormGroup>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              buttonSize={ButtonSize.MEDIUM}
              enableRedesign={true}
              showInventory={true}
              value={folderId}
              onChange={(e) => {
                setFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
              // Modal only creates but does not register entities, and all unregistered entities require a
              // project folder
              allowNone={false}
            />
          </FormGroup>
        </Col>

        {BenchlingConfig.STUDIES_ENABLE && (
          <Col sm={6}>
            <FormGroup>
              <FieldLabel label="Studies" />
              <StudySearchInput
                onChange={(studySearchItems: Array<StudySearchItem>) => {
                  setStudySearchItems(studySearchItems);
                }}
                shouldAllowMultiple={true}
                selectedItems={studySearchItems ?? []}
              />
            </FormGroup>
          </Col>
        )}
      </Row>

      <NucleotideTypeToggle
        nucleotideType={nucleotideType}
        onChangeSelection={(nucleotideType) => {
          setNucleotideType(nucleotideType);
          setBasesCoerced(false);
          // the bases were already sanitized when entered, but we can also use util.sanitize
          // to handle transcribing or reverse-transcribing DNA<->RNA
          if (notation === Notation.NATURAL_BASES) {
            setBases(util.sanitize(bases, nucleotideType, isFullUTFlexibilityEnabled()));
          }
        }}
      />

      <FormGroup>
        <FieldLabel label="Schema" />
        <TagSchemaSelector
          allowNone={true}
          oligoAwareFolderItemTypes={[OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[nucleotideType]]}
          onChange={(tagSchema) => {
            setTagSchemaId(tagSchema ? tagSchema.id : undefined);
          }}
          selectedTagSchemaId={tagSchemaId}
          forceVisibleSelectedTagSchema={null}
        />
      </FormGroup>

      {BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES && (
        <ResidueNotationDropdown
          notation={notation}
          onSelectNotation={(newNotation: Notation) => {
            setNotation(newNotation);
            if (newNotation === Notation.NATURAL_BASES) {
              setBases(util.sanitize(bases, nucleotideType, isFullUTFlexibilityEnabled()));
            }
          }}
          selectedCustomSyntaxId={customSyntaxId}
          onSelectCustomSyntaxId={setCustomSyntaxId}
          componentName="CreateOligoModal"
        />
      )}
      <FormGroup>
        <TextField
          label="Bases"
          labelStyle={LabelStyle.LABEL}
          error={
            basesCoerced
              ? {
                  message: getBaseCoercionWarningText(nucleotideType),
                  style: InputErrorMessageStyle.INFO,
                }
              : null
          }
          isRequired={true}
          value={bases}
          onBlur={() => {
            logIfFieldValueChange(bases, prevBases, setPrevBases, 'bases');
          }}
          onChange={(e) => {
            const unsanitizedBases = e.target.value;
            if (notation === Notation.NATURAL_BASES) {
              const {
                sanitizedBases,
                errors: {convertedUToT, convertedTToU},
              } = getSanitizedBasesAndSanitizationErrors(
                unsanitizedBases,
                nucleotideType,
                isFullUTFlexibilityEnabled()
              );
              if (convertedUToT || convertedTToU) {
                setBasesCoerced(true);
              }
              setBases(sanitizedBases);
            } else {
              setBases(unsanitizedBases);
            }
          }}
          dataTestAttrs={{component: 'CreateOligoModal', element: 'basesInput'}}
        />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Color" />
        <div className="add-oligo-row">
          <ColorPicker color={color} onSelectColor={setColor} />
        </div>
      </FormGroup>
    </Modal>
  );
}

export default CreateOligoModal;
